<script lang="ts">
	import type { GlobalSettingsType } from '$lib/queries/GlobalSettingsQuery';
	import { HTMLText } from '@atoms';
	import OvalTextSpinner from '@icons/OvalTextSpinner.svelte';
	import { Breakpoints, Theme } from '@tools';
	import FormSubmitButton from '@atoms/FormSubmitButton.svelte';
	import Smil from '@icons/Smil.svelte';

	let { data }: { data: GlobalSettingsType['footer'] } = $props();

	const theme = new Theme(data.colorTheme);
	let loading = $state(false);
	let status = $state<number | undefined>(undefined);
	let uiState = $state<'passive' | 'success' | 'loading' | 'error'>('passive');

	const logoOrientation = $derived(Breakpoints.md ? 'landscape' : 'portrait');

	const submitNewsletter = async (e: SubmitEvent) => {
		const form = e.target as HTMLFormElement | null;
		const email = (form?.querySelector('input[type="email"]') as HTMLInputElement | null)?.value;
		if (!form || !email) return;
		e.preventDefault();
		loading = true;
		status = undefined;

		fetch('/api/v2/newsletter', {
			method: 'POST',
			body: JSON.stringify({ email, lists: ['newsletter'] })
		})
			.then((res) => {
				status = res.status;
			})
			.catch((error) => {
				status = 500;
				console.error(error);
			})
			.finally(() => {
				loading = false;
				form?.reset();
			});
	};
</script>

<footer style={theme.css}>
	<form class="newsletter" onsubmit={submitNewsletter}>
		<h4 class="text:h4">Tilmeld dig Musikhusets nyhedsbrev</h4>
		<div class="input">
			<input
				type="email"
				name="email"
				class="text input:string"
				placeholder={uiState === 'success' ? 'Tak for din tilmelding!' : 'Email'}
			/>
			<FormSubmitButton text={'Tilmeld'} {loading} {status} bind:uiState />
		</div>
	</form>
	<div class="sitemap">
		{#each data.columns as column}
			<div class="column text:body">
				<h5>{column.title}</h5>
				<HTMLText text={column.content} />
			</div>
		{/each}

		<div class="logo-smil-mobile">
			<Smil orientation={logoOrientation} />
		</div>
	</div>

	<div class="bottom-right">
		<div class="links text:body">
			{#each data.bottomLinks as link}
				<a {...link}>{link.title}</a>
			{/each}
		</div>
		<div class="logo-smil-desktop">
			<Smil orientation={logoOrientation} />
		</div>
	</div>
	<OvalTextSpinner />
</footer>

<style lang="scss">
	footer {
		background-color: var(--theme-primary);
		color: var(--theme-foreground);
		display: flex;
		flex-direction: column;
		align-items: stretch;
		gap: var(--gap);
		overflow: hidden;
		padding: 0 var(--gap);
		padding-top: $gap8;
		margin-top: auto;

		:global(.oval-text-spinner) {
			margin-bottom: -40%;
			width: 100%;
		}

		@include md {
			display: grid;
			grid-template-columns: repeat(12, 1fr);
			grid-template-rows: auto auto;
			grid-template-areas:
				'newsletter newsletter newsletter newsletter sitemap sitemap sitemap sitemap sitemap sitemap sitemap sitemap'
				'graphic graphic graphic graphic graphic rights rights rights rights rights rights rights';
			gap: var(--spacing-auto) var(--gap);
			padding-top: $gap9;

			:global(.oval-text-spinner) {
				width: unset;
				max-width: 100%;
				grid-area: graphic;
			}
		}
	}

	.newsletter {
		display: flex;
		flex-direction: column;
		gap: $gap6;
		margin-bottom: $gap7;

		@include md {
			grid-area: newsletter;
			margin-bottom: 0;
			gap: $gap6;
		}

		:global(button) {
			flex: 0 0 auto;
		}
	}

	.input {
		display: flex;
		gap: $gap4;
	}

	.text {
		flex: 1 1 0;
	}

	h4 {
		font-family: $rootFamily;
		text-wrap: wrap;
	}

	.sitemap {
		display: flex;
		flex-wrap: wrap;
		gap: $gap5 var(--gap);

		@include md {
			grid-area: sitemap;
			flex-wrap: nowrap;
			justify-content: flex-end;
		}
	}

	.column {
		flex: 0 0 auto;
		width: 100%;
		hyphens: auto;

		@include sm {
			width: cols(2, 1, false, false);
		}

		@include md {
			width: cols(8, 2, false, false);
		}

		:global(a:not([href^='mailto:'])) {
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	h5 {
		font-weight: 500;
		margin-bottom: $gap2;

		@include md {
			margin-bottom: $gap3;
		}
	}

	.bottom-right {
		display: flex;
		flex-direction: column;
		margin-top: $gap5;
		margin-bottom: $gap7;
		gap: 1em;
		@include md {
			margin-top: 0;
			grid-area: rights;
			align-self: end;
			justify-self: end;
		}
	}

	.logo-smil-mobile {
		position: absolute;
		display: flex;
		width: 10%;
		right: $gap4;
		@include md {
			display: none;
		}
	}

	.logo-smil-desktop {
		display: none;
		justify-content: flex-end;
		@include md {
			display: flex;
		}
	}

	.links {
		display: flex;
		flex-direction: column;
		gap: 1em;
		margin-bottom: $gap4;

		@include md {
			margin-top: 0;
			flex-direction: row;
			grid-area: rights;
			align-self: end;
			justify-self: end;
		}
	}
</style>
